import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, Grid, TextField, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import moment from 'moment/moment';
import { styles } from './styles';
import DateCalendar from '../../../components/common/DateCalendar';
import { useChangeChangeRequestStatusMutation, useCancelChangeRequestMutation } from '../../../api/changeRequests';
import PositionCodesDisplay, { getPositionsInRequisition } from '../PositionCodesDisplay';

export const allowedChangeRequestActions = (actionType, changeRequest, data) => {
    if (actionType === 'create_policy') {
        if (data?.type === 'bulk') {
            return data?.position?.every(
                (item) =>
                    item?.position?.['changeRequestPolicy'] &&
                    ['Draft', 'Offer Released', 'Offer Accepted', 'Filled'].indexOf(item?.position?.['status']) < 0,
            );
        } else
            return (
                data?.position?.['changeRequestPolicy'] &&
                ['Draft', 'Offer Released', 'Offer Accepted', 'Filled'].indexOf(data?.position?.['status']) < 0
            );
    }
    if (actionType === 'modify_policy')
        return (
            changeRequest?.status === 'Active' && changeRequest?.createdBy?.employeeId === parseInt(data?.user?.empDBID)
        );
    else if (actionType === 'cancel')
        return (
            changeRequest?.status === 'Active' && changeRequest?.createdBy?.employeeId === parseInt(data?.user?.empDBID)
        );
    else if (actionType === 'approve') return true;
    else if (actionType === 'reject') return true;
    return false;
};

export const ChangeRequestMenu = ({ Component, row, tab, handleAction }) => {
    const [hasApproverRole, setHasApproverRole] = useState(false);
    const [actionType, setActionType] = useState();
    const [modalType, setModalType] = useState(1);
    const [startDate, setStartDate] = useState();
    const [comment, setComment] = useState('');
    const currentUser = useSelector((state) => state.session.user);
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [changeChangeRequestStatus, { isLoading: isStatusLoading }] = useChangeChangeRequestStatusMutation();
    const [cancelChangeRequest, { isLoading: isCancelLoading }] = useCancelChangeRequestMutation();

    useEffect(() => {
        setModalType(1);
        setStartDate();
        setComment('');
    }, [actionType]);

    useEffect(() => {
        const approvers = row?.position?.changeRequestPolicy?.approvers;
        const currentCRCategory = row?.categoryType?.name;
        if (currentUser?.empDBID && currentCRCategory && Array.isArray(approvers) && approvers.length > 0) {
            const currentCRCategoryApprover = approvers.find(
                (item) =>
                    item?.categoryType?.name === currentCRCategory &&
                    parseInt(currentUser?.empDBID) === item?.employeeId,
            );
            if (currentCRCategoryApprover) setHasApproverRole(true);
        }
    }, [currentUser?.empDBID, row?.position?.changeRequestPolicy?.approvers]);

    const { allowUpdatePositionStartDate, position } = useMemo(() => {
        const position = row?.position;
        const categoryType = row?.categoryType?.name;
        const majorApprovers = position?.changeRequestPolicy?.approvers?.filter(
            (item) => item?.categoryType?.name === 'Major',
        );
        const lastApprover = majorApprovers?.length > 0 ? majorApprovers[majorApprovers.length - 1] : null;
        const allowUpdatePositionStartDate =
            categoryType === 'Major' && lastApprover?.employeeId === parseInt(currentUser?.empDBID);
        return { allowUpdatePositionStartDate, position };
    }, [row, currentUser]);

    const positionsInRequisition = useMemo(() => getPositionsInRequisition(row), [row]);

    const handleConfirmStatus = async (status) => {
        if (isStatusLoading || isCancelLoading) return;

        try {
            const payload = {
                actType: status,
                ...(startDate && { startDate }),
                ...(comment?.length > 0 && { comment }),
            };

            const body = new FormData();
            body.append('changeRequestId', row?.id);

            if (payload.actType !== 'Cancel') {
                Object.keys(payload).forEach((key) => body.append(key, payload[key]));

                const response = await changeChangeRequestStatus(body).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                enqueueSnackbar(response?.message || 'Status updated successfully!', { variant: 'success' });
            } else {
                body.append('rejectionComment', payload?.comment);

                const response = await cancelChangeRequest(body).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                enqueueSnackbar(response?.message || 'Change request cancelled successfully!', { variant: 'success' });
            }

            handleAction();
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Failed to update change request.', { variant: 'error' });
        } finally {
            setActionType();
        }
    };

    const dialogContent = useMemo(() => {
        if (actionType === 'approve') {
            switch (modalType) {
                case 1:
                    return {
                        title: 'Approve change request',
                        subTitle: 'Are you sure you want to approve the change request?',
                        children: positionsInRequisition ? (
                            <PositionCodesDisplay
                                positionsInRequisition={positionsInRequisition}
                                fallbackCode={position?.positionCode}
                                align="center"
                                noteVariant="body2"
                            />
                        ) : null,
                        actions: (
                            <>
                                <Button variant="text" color="primary" onClick={() => setActionType()}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        return allowUpdatePositionStartDate
                                            ? setModalType(2)
                                            : handleConfirmStatus('Approve');
                                    }}
                                >
                                    {allowUpdatePositionStartDate ? `Next` : `Approve`}
                                </Button>
                            </>
                        ),
                    };
                case 2:
                    return {
                        title: 'Reset Start Date',
                        subTitle: `Would you like to reset the start date for this position (${position?.positionCode})?`,
                        actions: (
                            <>
                                <Button variant="text" color="primary" onClick={() => handleConfirmStatus('Approve')}>
                                    Skip
                                </Button>
                                <Button variant="contained" color="primary" onClick={() => setModalType(3)}>
                                    Yes
                                </Button>
                            </>
                        ),
                    };
                case 3:
                    return {
                        title: 'Update Position Start Date',
                        subTitle: '',
                        children: (
                            <Box>
                                <Box sx={styles.infoLine}></Box>
                                <Typography
                                    variant={'body2'}
                                    mt="4px"
                                >{`Position Code: ${position?.positionCode}`}</Typography>
                                <Typography
                                    variant={'body2'}
                                    mt="4px"
                                >{`Current Start Date: ${position?.startDate ? moment(position?.startDate).format('MMM DD yyyy') : '-'}`}</Typography>
                                <Box sx={styles.infoLine}></Box>
                                <Typography variant="subtitle1">Select the updated date from calendar</Typography>
                                <DateCalendar
                                    value={startDate || position?.startDate}
                                    onChange={(_key, value) => setStartDate(value)}
                                    disableFuture={true}
                                />
                            </Box>
                        ),
                        actions: (
                            <>
                                <Button variant="text" color="primary" onClick={() => setActionType()}>
                                    Cancel
                                </Button>
                                <Button
                                    disabled={
                                        !startDate ||
                                        (position?.startDate && moment(position?.startDate).format('yyyy-MM-DD')) ===
                                            (startDate && moment(startDate).format('yyyy-MM-DD'))
                                    }
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setModalType(4)}
                                >
                                    Update
                                </Button>
                            </>
                        ),
                    };
                case 4:
                    return {
                        title: 'Approve Change Request and Confirm Position Start Date',
                        subTitle: 'Are you sure you want to Approve Change Request and update position details of',
                        children: (
                            <Grid container spacing={1}>
                                <Grid item xs={12}>
                                    <Box sx={styles.infoLine}></Box>
                                    <Typography
                                        variant={'body2'}
                                        mx="24px"
                                    >{`Position Code: ${position?.positionCode}`}</Typography>
                                    <Typography
                                        variant={'body2'}
                                        mx="24px"
                                        mt="6px"
                                    >{`Current Start Date: ${position?.startDate ? moment(position?.startDate).format('MMM DD yyyy') : '-'}`}</Typography>
                                    <Typography
                                        variant={'body2'}
                                        mx="24px"
                                        mt="6px"
                                        color={'#3F455A'}
                                    >{`Updated Start Date: ${startDate ? moment(startDate).format('MMM DD yyyy') : '-'}`}</Typography>
                                    <Box sx={styles.infoLine}></Box>
                                </Grid>
                            </Grid>
                        ),
                        actions: (
                            <>
                                <Button variant="text" color="primary" onClick={() => setActionType()}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleConfirmStatus('Approve')}
                                >
                                    Confirm
                                </Button>
                            </>
                        ),
                    };
            }
        } else if (actionType === 'reject')
            return {
                title: 'Reject Change Request',
                subTitle: `Are you sure you want to reject the change request?`,
                children: (
                    <>
                        {positionsInRequisition ? (
                            <PositionCodesDisplay
                                positionsInRequisition={positionsInRequisition}
                                fallbackCode={position?.positionCode}
                                align="center"
                                noteVariant="body2"
                            />
                        ) : null}
                        <TextField
                            sx={{ mt: '15px' }}
                            fullWidth
                            label="Reason"
                            placeholder="Type here"
                            size="small"
                            variant="outlined"
                            onChange={(e) => setComment(e.target.value)}
                            multiline
                            rows={5}
                        />
                    </>
                ),
                actions: (
                    <React.Fragment>
                        <Button size="large" onClick={() => setActionType()}>
                            Cancel
                        </Button>
                        <Button size="large" variant="contained" onClick={() => handleConfirmStatus('Revert')}>
                            Confirm
                        </Button>
                    </React.Fragment>
                ),
            };
        else if (actionType === 'cancel')
            return {
                title: 'Cancel Change Request',
                subTitle: `Are you sure you want to cancel the change request?`,
                confirmation: `Once cancelled, the change request would become unactionable, and no further actions can be taken on it.`,
                children: (
                    <TextField
                        sx={{ mt: '15px' }}
                        fullWidth
                        label="Reason"
                        placeholder="Type here"
                        size="small"
                        variant="outlined"
                        onChange={(e) => setComment(e.target.value)}
                        multiline
                        rows={5}
                    />
                ),
                actions: (
                    <React.Fragment>
                        <Button size="large" onClick={() => setActionType()}>
                            Cancel
                        </Button>
                        <Button size="large" variant="contained" onClick={() => handleConfirmStatus('Cancel')}>
                            Confirm
                        </Button>
                    </React.Fragment>
                ),
            };
    }, [
        actionType,
        modalType,
        startDate,
        comment,
        allowUpdatePositionStartDate,
        position,
        positionsInRequisition,
    ]);

    const actionItems = [
        {
            label: 'Modify',
            type: 'modify_policy',
            icon: <EditIcon />,
            onClick: () => navigate(`/changeRequests/${row?.id}/modify/${row?.parentEntityId}`),
        },
        tab !== 'approver' && {
            label: 'Cancel',
            type: 'cancel',
            icon: <CloseIcon />,
            onClick: (action) => setActionType(action.type),
        },
        hasApproverRole &&
            tab === 'approver' && {
                label: 'Approve',
                type: 'approve',
                icon: <CheckIcon />,
                onClick: (action) => setActionType(action.type),
            },
        hasApproverRole &&
            tab === 'approver' && {
                label: 'Reject',
                type: 'reject',
                icon: <ClearIcon />,
                onClick: (action) => setActionType(action.type),
            },
    ];

    const filteredActionItems = actionItems.filter((action) =>
        allowedChangeRequestActions(action.type, row, { user: currentUser }),
    );

    return <Component actions={filteredActionItems} dialog={dialogContent} onDialogClose={() => setActionType()} />;
};
